/**
 * Capability 5: Governance (renamed from Operations)
 * Purpose: Provides evidence, auditability and oversight across every regulated interaction.
 * "This is how we evidence compliance." — the section regulators open first.
 */
import { useEffect, useState } from "react";
import CapabilityLandingPage from "@/components/capability/CapabilityLandingPage";
import api from "@/lib/api";
import {
  ShieldCheck, LineChart, Bell, FolderKanban, UserCheck, ListTodo, ScrollText, BarChart3,
} from "lucide-react";

export default function GovernanceCapability() {
  const [kpis, setKpis] = useState({ evidence: null, audit: null, certs: null, reports: null });

  useEffect(() => {
    (async () => {
      const next = { evidence: null, audit: null, certs: null, reports: null };
      try { const { data } = await api.get("/evidence"); next.evidence = Array.isArray(data) ? data.length : (data?.total ?? null); } catch { /* endpoint optional */ }
      try { const { data } = await api.get("/admin/audit"); next.audit = Array.isArray(data) ? data.length : null; } catch { /* endpoint optional */ }
      // Certs + reports counters — placeholder unless endpoint exists
      setKpis(next);
    })();
  }, []);

  return (
    <CapabilityLandingPage
      eyebrow="Capability 5 · Governance"
      title="Governance"
      purpose="Provides evidence, auditability and oversight across every regulated interaction. When a regulator or auditor arrives, this is the surface that answers 'prove it.'"
      primaryActions={[
        { label: "Open Evidence Vault", to: "/evidence", icon: ShieldCheck },
        { label: "View Audit Trail", to: "/audit-trail", icon: ScrollText },
      ]}
      kpis={[
        { label: "Evidence records", value: kpis.evidence, subLabel: "hash-chained" },
        { label: "Audit entries (7d)", value: kpis.audit, subLabel: "immutable" },
        { label: "Trust certificates", value: null, emptyHint: "Wire on /trust/count" },
        { label: "Reports run (30d)", value: null, emptyHint: "Metrics wiring pending" },
      ]}
      subCapabilities={[
        // Order matters — Evidence is the first thing regulators look for.
        { name: "Evidence Vault", to: "/evidence", icon: ShieldCheck, status: "live",
          description: "Every decision, document and consent — signed and hash-chained." },
        { name: "Outcome Governance", to: "/governance/outcomes", icon: LineChart, status: "live",
          description: "Continuous conduct monitoring — evaluate every completed Trust Request against fair-outcome rules." },
        { name: "Outcome Monitoring", to: "/outcome-monitoring", icon: LineChart, status: "future",
          description: "Track TCF outcomes: did consumers actually understand?" },
        { name: "Cases", to: "/cases", icon: FolderKanban, status: "future",
          description: "Complaints, disputes, escalations — every case with an audit chain." },
        { name: "Human Reviews", to: "/human-reviews", icon: UserCheck, status: "future",
          description: "Where automated decisions hand off to a human reviewer." },
        { name: "Tasks", to: "/tasks", icon: ListTodo, status: "future",
          description: "Assigned actions with SLA tracking across the platform." },
        { name: "Audit Trail", to: "/audit-trail", icon: ScrollText, status: "placeholder",
          description: "Search every audited event — who, what, when, tenant, IP." },
        { name: "Alerts", to: "/alerts", icon: Bell, status: "future",
          description: "High-risk pattern alerts (scam, vulnerable customer, RG threshold)." },
        { name: "Reports", to: "/reports", icon: BarChart3, status: "live",
          description: "Regulator-ready reports, exportable per tenant with hash proof." },
      ]}
      relatedCapabilities={[
        { name: "Trust Workflows", to: "/capability/trust-workflows" },
        { name: "Platform · Audit Logs", to: "/capability/platform" },
      ]}
    />
  );
}
